import { Box, Button } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { usePageAlerts } from "@outschool/ui-components-website";
import React from "react";

import {
  UpdatePaymentMethodDueToPaymentFailureModal,
  useEnrollmentsPausedDueToPaymentFailure
} from "../account/UpdatePaymentMethodDueToPaymentFailure";

export const usePaymentFailurePageAlert = currentUser => {
  const paymentFailureFeatureFlag = useFeatureFlag(
    "ppp-ongoing-enrollment-payment-failure-pause-rollout"
  );
  const { addPageAlert, removePageAlert, muteAlert } = usePageAlerts();
  const { t } = useTranslation(
    "client\\components\\alerts\\PaymentFailurePageAlert"
  );
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] =
    React.useState(false);
  const { data } = useEnrollmentsPausedDueToPaymentFailure();
  const enrollmentsPausedDueToPaymentFailure =
    data?.currentUser?.activeOngoingEnrollmentsPausedDueToPaymentFailure;

  React.useEffect(() => {
    if (
      currentUser?.shouldShowEnrollmentPausedDueToPaymentFailureAlert &&
      paymentFailureFeatureFlag &&
      data &&
      enrollmentsPausedDueToPaymentFailure
    ) {
      // Sort the enrollments based on their pause window start time
      // We know they're currently paused, so we can use ongoingEndTime
      // Sort so that first element is earliest start time, last element is latest
      const enrollmentsSortedByPauseWindowStartTime =
        enrollmentsPausedDueToPaymentFailure.length > 1
          ? enrollmentsPausedDueToPaymentFailure?.sort((a, b) =>
              dayjs(a.ongoingEndTime).diff(b.ongoingEndTime)
            )
          : enrollmentsPausedDueToPaymentFailure;
      // Get earliest pause window start time
      const pauseWindowStartTime = dayjs(
        enrollmentsSortedByPauseWindowStartTime[0].ongoingEndTime
      ).toString();

      // Set the id to include the pauseWindowStartTime so that there is a unique PageAlert for each two week window, allowing us to mute it
      const updatePaymentMethodAlertId =
        "UpdatePaymentMethod" + pauseWindowStartTime;

      addPageAlert({
        id: updatePaymentMethodAlertId,
        severity: "error",
        title: t("Subscriptions paused due to payment issue"),
        content: t(
          "Your weekly subscriptions have been paused. To resume your subscription and regain full access to these classes, please update your payment method."
        ),
        dismissable: false,
        persistant: true,
        action: (
          <Box
            sx={{
              // We need 12px of top padding so that the Alert action is in line with the Alert title and icon
              // This should probably be done in the Alert component itself? Just not sure how.
              paddingTop: "12px"
            }}
          >
            <Button
              variant="link"
              color="error"
              onClick={() => setIsPaymentMethodModalOpen(true)}
            >
              {t("Update payment method")}
            </Button>
            <UpdatePaymentMethodDueToPaymentFailureModal
              formOptions={{
                alertMessage: t(
                  "Update your payment method to resume weekly subscriptions paused due to payment issue."
                )
              }}
              open={isPaymentMethodModalOpen}
              onClose={() => setIsPaymentMethodModalOpen(false)}
              muteAlert={() => {
                removePageAlert(updatePaymentMethodAlertId);
                muteAlert(updatePaymentMethodAlertId);
              }}
            />
          </Box>
        )
      });
    }
  }, [
    addPageAlert,
    removePageAlert,
    muteAlert,
    currentUser?.shouldShowEnrollmentPausedDueToPaymentFailureAlert,
    paymentFailureFeatureFlag,
    data,
    enrollmentsPausedDueToPaymentFailure,
    isPaymentMethodModalOpen,
    t
  ]);
};
